const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const getToken = () => localStorage.getItem('token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

async function handle(res) {
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      detail = j.detail || detail;
    } catch (e) { /* ignore */ }
    // Attache le status HTTP à l'erreur pour permettre aux pages de discriminer 401/403.
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

const api = {
  // ========== PUBLIC CONFIG ==========
  getPublicConfig: async () => {
    try {
      const res = await fetch(`${API_URL}/api/config/public`);
      if (!res.ok) return { free_mode: false };
      return res.json();
    } catch (e) {
      return { free_mode: false };
    }
  },

  getPublicStats: async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/stats`);
      if (!res.ok) throw new Error('stats unavailable');
      return res.json();
    } catch (e) {
      // Valeurs de repli — synchronisées avec la base au moment du déploiement.
      // Le backend renverra toujours les chiffres réels en production.
      return {
        questions: 719,
        themes: 9,
        modules: 38,
        questions_per_exam: 40,
        exam_pass_threshold: 35,
      };
    }
  },

  // ========== HERO EDITIONS — concept magazine hebdomadaire ==========
  getCurrentHeroEdition: async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/hero/current`);
      if (!res.ok) throw new Error('hero edition unavailable');
      return res.json();
    } catch (e) {
      return null; // Le composant Home gère le fallback statique si null
    }
  },

  // --- Annuaire partenaires : bannière promo (ex: Formation B96) ---
  adminGetPartnersPromoBanner: async (dept) => {
    const res = await fetch(`${API_URL}/api/admin/annuaire/partners/promo-banner?dept=${encodeURIComponent(dept)}`, { headers: authHeaders() });
    return handle(res);
  },

  adminUpdatePartnersPromoBanner: async (dept, banner) => {
    const res = await fetch(`${API_URL}/api/admin/annuaire/partners/promo-banner`, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ dept, banner }),
    });
    return handle(res);
  },


  adminListHeroEditions: async () => {
    const res = await fetch(`${API_URL}/api/admin/hero-editions`, { headers: authHeaders() });
    return handle(res);
  },

  adminCreateHeroEdition: async (payload) => {
    const res = await fetch(`${API_URL}/api/admin/hero-editions`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },

  adminUpdateHeroEdition: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/hero-editions/${id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },

  adminDeleteHeroEdition: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/hero-editions/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return handle(res);
  },

  adminPublishHeroEdition: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/hero-editions/${id}/publish`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handle(res);
  },

  adminPromoteFromMagazine: async (payload) => {
    const res = await fetch(`${API_URL}/api/admin/hero-editions/promote-from-magazine`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },

  // ========== NEWSLETTER ==========
  newsletterSubscribe: async (email, source = 'home') => {
    const res = await fetch(`${API_URL}/api/public/newsletter/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, source, consent: true }),
    });
    return handle(res);
  },

  newsletterUnsubscribe: async (email) => {
    const res = await fetch(`${API_URL}/api/public/newsletter/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, consent: true }),
    });
    return handle(res);
  },

  // ========== CONTACT PUBLIC ==========
  publicContact: async ({ name, email, subject, message, website }) => {
    const res = await fetch(`${API_URL}/api/public/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        email,
        subject: subject || '',
        message,
        consent: true,
        website: website || '', // honeypot anti-bot
      }),
    });
    return handle(res);
  },

  adminListNewsletter: async () => {
    const res = await fetch(`${API_URL}/api/admin/newsletter/subscribers`, { headers: authHeaders() });
    return handle(res);
  },

  adminDeleteNewsletterSubscriber: async (email) => {
    const res = await fetch(`${API_URL}/api/admin/newsletter/subscribers/${encodeURIComponent(email)}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return handle(res);
  },

  adminNewsletterCsvUrl: (onlyActive = true) =>
    `${API_URL}/api/admin/newsletter/export.csv?only_active=${onlyActive}`,

  // ========== MAILING ad-hoc (admin) ==========
  adminMailingSend: async (payload) => {
    const res = await fetch(`${API_URL}/api/admin/mailing/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },

  adminMailingCampaigns: async () => {
    const res = await fetch(`${API_URL}/api/admin/mailing/campaigns`, { headers: authHeaders() });
    return handle(res);
  },

  adminMailingQuota: async () => {
    const res = await fetch(`${API_URL}/api/admin/mailing/quota`, { headers: authHeaders() });
    return handle(res);
  },

  adminMailingDiagnostic: async () => {
    const res = await fetch(`${API_URL}/api/admin/mailing/diagnostic`, { headers: authHeaders() });
    return handle(res);
  },

  adminMailingSetApiKey: async (api_key) => {
    const res = await fetch(`${API_URL}/api/admin/mailing/api-key`, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ api_key }),
    });
    return handle(res);
  },

  adminMailingTestApiKey: async (api_key, to_email = null) => {
    const res = await fetch(`${API_URL}/api/admin/mailing/test-api-key`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ api_key, to_email }),
    });
    return handle(res);
  },

  adminMailingGetSender: async () => {
    const res = await fetch(`${API_URL}/api/admin/mailing/sender`, { headers: authHeaders() });
    return handle(res);
  },

  adminMailingSetSender: async (email, name = null) => {
    const res = await fetch(`${API_URL}/api/admin/mailing/sender`, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name }),
    });
    return handle(res);
  },

  adminChangePassword: async (current_password, new_password) => {
    const res = await fetch(`${API_URL}/api/admin/me/change-password`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ current_password, new_password }),
    });
    return handle(res);
  },

  // ========== CONTACT REQUESTS (admin) ==========
  adminListContactRequests: async (statusFilter = null) => {
    const url = statusFilter
      ? `${API_URL}/api/admin/contact-requests?status=${encodeURIComponent(statusFilter)}`
      : `${API_URL}/api/admin/contact-requests`;
    const res = await fetch(url, { headers: authHeaders() });
    return handle(res);
  },

  adminUpdateContactRequest: async (id, status) => {
    const res = await fetch(`${API_URL}/api/admin/contact-requests/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ status }),
    });
    return handle(res);
  },

  adminReplyContactRequest: async (id, replyMessage) => {
    const res = await fetch(`${API_URL}/api/admin/contact-requests/${encodeURIComponent(id)}/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ reply_message: replyMessage }),
    });
    return handle(res);
  },

  // ========== EMAIL LOGS (admin) ==========
  adminListEmailLogs: async (statusFilter = null, limit = 100) => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    params.set('limit', String(limit));
    const res = await fetch(`${API_URL}/api/admin/email-logs?${params.toString()}`, {
      headers: authHeaders(),
    });
    return handle(res);
  },

  // ========== AUTH ==========
  register: async (email, password, name, teacherCode) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, teacher_code: teacherCode || null })
    });
    return handle(res);
  },
  login: async (email, password) => {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: formData
    });
    return handle(res);
  },
  teacherCodeLogin: async (code, pseudo) => {
    const res = await fetch(`${API_URL}/api/auth/teacher-code-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, pseudo: pseudo || null })
    });
    return handle(res);
  },
  getMe: async () => {
    const res = await fetch(`${API_URL}/api/auth/me`, { headers: authHeaders() });
    return handle(res);
  },

  // ========== FORMATIONS / MODULES ==========
  getFormations: async () => {
    const res = await fetch(`${API_URL}/api/formations`);
    return handle(res);
  },
  getModules: async ({ formation, search, sidefilter } = {}) => {
    const params = new URLSearchParams();
    if (formation) params.append('formation', formation);
    if (search) params.append('search', search);
    if (sidefilter) params.append('sidefilter', sidefilter);
    const token = getToken();
    const res = await fetch(`${API_URL}/api/modules?${params}`, {
      headers: token ? authHeaders() : {}
    });
    return handle(res);
  },
  getModule: async (id) => {
    const token = getToken();
    const res = await fetch(`${API_URL}/api/modules/${id}`, {
      headers: token ? authHeaders() : {}
    });
    return handle(res);
  },

  // ========== PROGRESSION ==========
  getProgress: async () => {
    const res = await fetch(`${API_URL}/api/progress/me`, { headers: authHeaders() });
    return handle(res);
  },
  toggleFavorite: async (moduleId) => {
    const res = await fetch(`${API_URL}/api/progress/toggle-favorite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ module_id: moduleId })
    });
    return handle(res);
  },
  toggleSeen: async (moduleId) => {
    const res = await fetch(`${API_URL}/api/progress/toggle-seen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ module_id: moduleId })
    });
    return handle(res);
  },
  recordQCM: async (isCorrect) => {
    const res = await fetch(`${API_URL}/api/progress/qcm-attempt`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ is_correct: isCorrect })
    });
    return handle(res);
  },
  resetQCM: async () => {
    const res = await fetch(`${API_URL}/api/progress/reset-qcm`, {
      method: 'POST',
      headers: authHeaders()
    });
    return handle(res);
  },

  // ========== GAMIFICATION (kilomètres / streaks / trophées) ==========
  getGamification: async () => {
    const res = await fetch(`${API_URL}/api/progress/gamification`, { headers: authHeaders() });
    return handle(res);
  },
  addGamificationXp: async (amount) => {
    const res = await fetch(`${API_URL}/api/progress/gamification/add-xp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ amount: Math.max(1, Math.floor(amount || 0)) })
    });
    return handle(res);
  },
  pingGamificationStreak: async () => {
    const res = await fetch(`${API_URL}/api/progress/gamification/ping-streak`, {
      method: 'POST',
      headers: authHeaders()
    });
    return handle(res);
  },
  unlockGamificationTrophy: async (trophy) => {
    const res = await fetch(`${API_URL}/api/progress/gamification/unlock-trophy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        id: trophy.id,
        name: trophy.name || null,
        color: trophy.color || null,
        icon: trophy.icon || null,
      })
    });
    return handle(res);
  },
  migrateGamification: async (localState) => {
    const res = await fetch(`${API_URL}/api/progress/gamification/migrate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        xp: localState.xp || 0,
        streak: localState.streak || 0,
        last_activity_date: localState.lastActivityDate || null,
        trophies: (localState.trophies || []).map(t => ({
          id: t.id,
          name: t.name || null,
          color: t.color || null,
          icon: t.icon || null,
          unlocked_at: t.unlockedAt || t.unlocked_at || null,
        }))
      })
    });
    return handle(res);
  },
  resetGamification: async () => {
    const res = await fetch(`${API_URL}/api/progress/gamification/reset`, {
      method: 'POST',
      headers: authHeaders()
    });
    return handle(res);
  },

  // ========== LIVRET D'APPRENTISSAGE — Auto-évaluation élève (REMC C1-C4) ==========
  getLivretProgression: async () => {
    const res = await fetch(`${API_URL}/api/livret/progression`, { headers: authHeaders() });
    return handle(res);
  },
  updateLivretItem: async (code, { status, notes } = {}) => {
    const body = {};
    if (status !== undefined) body.status = status;
    if (notes !== undefined) body.notes = notes;
    const res = await fetch(`${API_URL}/api/livret/progression/${encodeURIComponent(code)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    return handle(res);
  },
  resetLivretProgression: async () => {
    const res = await fetch(`${API_URL}/api/livret/progression/reset`, {
      method: 'POST',
      headers: authHeaders()
    });
    return handle(res);
  },

  // ========== MAGAZINE ETM — Tracking lecture & complétion des 9 numéros ==========
  getEtmProgression: async () => {
    const res = await fetch(`${API_URL}/api/etm/progression`, { headers: authHeaders() });
    return handle(res);
  },
  markEtmRead: async (slug) => {
    const res = await fetch(`${API_URL}/api/etm/progression/${encodeURIComponent(slug)}/read`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handle(res);
  },
  markEtmComplete: async (slug, { score, total, passingScore = 8 }) => {
    const res = await fetch(`${API_URL}/api/etm/progression/${encodeURIComponent(slug)}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ score, total, passing_score: passingScore }),
    });
    return handle(res);
  },

  // ========== THEMES / QUESTIONS (Révision thématique) ==========
  getThemes: async () => {
    const res = await fetch(`${API_URL}/api/themes`);
    return handle(res);
  },
  getQuestionsByTheme: async (themeCode, limit = 20) => {
    const params = new URLSearchParams();
    if (themeCode) params.append('theme', themeCode);
    if (limit) params.append('limit', String(limit));
    const res = await fetch(`${API_URL}/api/questions?${params}`);
    return handle(res);
  },
  getExamQuestions: async (size = 40) => {
    const res = await fetch(`${API_URL}/api/questions/exam?size=${size}`);
    return handle(res);
  },
  getQuestionsByIds: async (ids) => {
    const res = await fetch(`${API_URL}/api/questions/by-ids`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids })
    });
    return handle(res);
  },
  recordQCMDetailed: async (isCorrect, questionId, themeCode) => {
    const res = await fetch(`${API_URL}/api/progress/qcm-attempt`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ is_correct: isCorrect, question_id: questionId || null, theme_code: themeCode || null })
    });
    return handle(res);
  },
  getErrors: async () => {
    const res = await fetch(`${API_URL}/api/progress/errors`, { headers: authHeaders() });
    return handle(res);
  },
  clearErrors: async () => {
    const res = await fetch(`${API_URL}/api/progress/errors/clear`, {
      method: 'POST',
      headers: authHeaders()
    });
    return handle(res);
  },
  removeError: async (questionId) => {
    const res = await fetch(`${API_URL}/api/progress/errors/${questionId}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    return handle(res);
  },

  // === TEACHER DASHBOARD ===
  getTeacherOverview: async (period = '30d') => {
    const res = await fetch(`${API_URL}/api/teacher/dashboard/overview?period=${period}`, { headers: authHeaders() });
    return handle(res);
  },
  getTeacherThemes: async (period = '30d') => {
    const res = await fetch(`${API_URL}/api/teacher/dashboard/themes?period=${period}`, { headers: authHeaders() });
    return handle(res);
  },
  getTeacherWorstQuestions: async (period = '30d', limit = 10) => {
    const res = await fetch(`${API_URL}/api/teacher/dashboard/worst-questions?period=${period}&limit=${limit}`, { headers: authHeaders() });
    return handle(res);
  },
  getTeacherModulesStats: async (period = '30d') => {
    const res = await fetch(`${API_URL}/api/teacher/dashboard/modules-stats?period=${period}`, { headers: authHeaders() });
    return handle(res);
  },
  getTeacherHeatmap: async (period = '30d') => {
    const res = await fetch(`${API_URL}/api/teacher/dashboard/heatmap?period=${period}`, { headers: authHeaders() });
    return handle(res);
  },
  getTeacherStrugglingStudents: async (period = '30d', threshold = 40) => {
    const res = await fetch(`${API_URL}/api/teacher/dashboard/struggling-students?period=${period}&threshold=${threshold}`, { headers: authHeaders() });
    return handle(res);
  },

  // === ADMIN ALERTS ===
  getAdminAlertsCount: async () => {
    const res = await fetch(`${API_URL}/api/admin/alerts/count`, { headers: authHeaders() });
    return handle(res);
  },
  getAdminAlerts: async ({ refresh = 0 } = {}) => {
    const res = await fetch(`${API_URL}/api/admin/alerts?refresh=${refresh}`, { headers: authHeaders() });
    return handle(res);
  },
  dismissAdminAlert: async (alertId, snoozeHours = 24) => {
    const res = await fetch(`${API_URL}/api/admin/alerts/${alertId}/dismiss?snooze_hours=${snoozeHours}`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handle(res);
  },
  refreshAdminAlerts: async () => {
    const res = await fetch(`${API_URL}/api/admin/alerts/refresh`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handle(res);
  },
  sendAdminAlertsDigest: async () => {
    const res = await fetch(`${API_URL}/api/admin/alerts/send-digest`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handle(res);
  },
  // Admin questions
  // === ADMIN AUDIT ===
  getAdminAuditSummary: async () => {
    const res = await fetch(`${API_URL}/api/admin/audit/summary`, { headers: authHeaders() });
    return handle(res);
  },
  getAdminAuditFindings: async ({ severity, status, theme, skip = 0, limit = 50 } = {}) => {
    const params = new URLSearchParams();
    if (severity) params.append('severity', severity);
    if (status) params.append('status', status);
    if (theme) params.append('theme', theme);
    params.append('skip', String(skip));
    params.append('limit', String(limit));
    const res = await fetch(`${API_URL}/api/admin/audit/findings?${params}`, { headers: authHeaders() });
    return handle(res);
  },
  applyAuditFinding: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/audit/findings/${id}/apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  rejectAuditFinding: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/audit/findings/${id}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  approveAuditFinding: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/audit/findings/${id}/approve`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handle(res);
  },

  // === COMMUNAUTÉ ECSR ===
  getForumCategories: async () => {
    const res = await fetch(`${API_URL}/api/community/forum/categories`, { headers: authHeaders() });
    return handle(res);
  },
  getForumThreads: async ({ category_slug, skip = 0, limit = 30 } = {}) => {
    const p = new URLSearchParams();
    if (category_slug) p.append('category_slug', category_slug);
    p.append('skip', String(skip));
    p.append('limit', String(limit));
    const res = await fetch(`${API_URL}/api/community/forum/threads?${p}`, { headers: authHeaders() });
    return handle(res);
  },
  getForumThreadDetail: async (id) => {
    const res = await fetch(`${API_URL}/api/community/forum/threads/${id}`, { headers: authHeaders() });
    return handle(res);
  },
  createForumThread: async (payload) => {
    const res = await fetch(`${API_URL}/api/community/forum/threads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  createForumPost: async (thread_id, body) => {
    const res = await fetch(`${API_URL}/api/community/forum/threads/${thread_id}/posts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ body }),
    });
    return handle(res);
  },
  moderateThread: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/community/forum/threads/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },

  // RESSOURCES
  createCommunityResource: async (payload) => {
    const res = await fetch(`${API_URL}/api/community/resources`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  listCommunityResources: async ({ type, theme_code, skip = 0, limit = 30 } = {}) => {
    const p = new URLSearchParams();
    if (type) p.append('type', type);
    if (theme_code) p.append('theme_code', theme_code);
    p.append('skip', String(skip));
    p.append('limit', String(limit));
    const res = await fetch(`${API_URL}/api/community/resources?${p}`, { headers: authHeaders() });
    return handle(res);
  },
  getCommunityResource: async (id) => {
    const res = await fetch(`${API_URL}/api/community/resources/${id}`, { headers: authHeaders() });
    return handle(res);
  },
  adminListPendingResources: async () => {
    const res = await fetch(`${API_URL}/api/admin/community/resources/pending`, { headers: authHeaders() });
    return handle(res);
  },
  adminApproveResource: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/community/resources/${id}/approve`, {
      method: 'POST', headers: authHeaders()
    });
    return handle(res);
  },
  adminRejectResource: async (id, reason) => {
    const res = await fetch(`${API_URL}/api/admin/community/resources/${id}/reject`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ reason: reason || '' })
    });
    return handle(res);
  },

  // QCM communautaires
  proposeCommunityQuestion: async (payload) => {
    const res = await fetch(`${API_URL}/api/community/questions`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  listCommunityQuestions: async () => {
    const res = await fetch(`${API_URL}/api/community/questions`, { headers: authHeaders() });
    return handle(res);
  },
  adminListPendingCommunityQuestions: async () => {
    const res = await fetch(`${API_URL}/api/admin/community/questions/pending`, { headers: authHeaders() });
    return handle(res);
  },
  adminApproveCommunityQuestion: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/community/questions/${id}/approve`, {
      method: 'POST', headers: authHeaders()
    });
    return handle(res);
  },
  adminRejectCommunityQuestion: async (id, reason) => {
    const res = await fetch(`${API_URL}/api/admin/community/questions/${id}/reject`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ reason: reason || '' })
    });
    return handle(res);
  },
  adminCommunitySummary: async () => {
    const res = await fetch(`${API_URL}/api/admin/community/summary`, { headers: authHeaders() });
    return handle(res);
  },

  getAdminQuestions: async ({ theme, difficulty, search, has_sign, sign_code, skip = 0, limit = 50 } = {}) => {
    const params = new URLSearchParams();
    if (theme) params.append('theme', theme);
    if (difficulty) params.append('difficulty', difficulty);
    if (search) params.append('search', search);
    if (has_sign) params.append('has_sign', has_sign);
    if (sign_code) params.append('sign_code', sign_code);
    params.append('skip', String(skip));
    params.append('limit', String(limit));
    const res = await fetch(`${API_URL}/api/admin/questions?${params}`, { headers: authHeaders() });
    return handle(res);
  },
  updateAdminQuestion: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/questions/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload)
    });
    return handle(res);
  },
  deleteAdminQuestion: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/questions/${id}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    return handle(res);
  },
  exportAdminQuestions: async (format = 'json', theme = '') => {
    const params = new URLSearchParams();
    params.append('format', format);
    if (theme) params.append('theme', theme);
    const res = await fetch(`${API_URL}/api/admin/questions/export?${params}`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Export failed');
    return res.blob();
  },
  getAdminPayments: async ({ provider, status, skip = 0, limit = 50 } = {}) => {
    const params = new URLSearchParams();
    if (provider) params.append('provider', provider);
    if (status) params.append('status', status);
    params.append('skip', String(skip));
    params.append('limit', String(limit));
    const res = await fetch(`${API_URL}/api/admin/payments?${params}`, { headers: authHeaders() });
    return handle(res);
  },

  // ========== PAYMENTS (Stripe) ==========
  getPackages: async () => {
    const res = await fetch(`${API_URL}/api/payments/packages`);
    return handle(res);
  },
  createCheckoutSession: async (packageId, originUrl) => {
    const res = await fetch(`${API_URL}/api/payments/checkout/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ package_id: packageId, origin_url: originUrl })
    });
    return handle(res);
  },
  getCheckoutStatus: async (sessionId) => {
    const res = await fetch(`${API_URL}/api/payments/checkout/status/${sessionId}`, { headers: authHeaders() });
    return handle(res);
  },
  getMyTransactions: async () => {
    const res = await fetch(`${API_URL}/api/payments/my-transactions`, { headers: authHeaders() });
    return handle(res);
  },

  // ========== PAYPAL ==========
  createPaypalOrder: async (packageId, originUrl) => {
    const res = await fetch(`${API_URL}/api/paypal/create-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ package_id: packageId, origin_url: originUrl })
    });
    return handle(res);
  },
  capturePaypalOrder: async (orderId) => {
    const res = await fetch(`${API_URL}/api/paypal/capture-order/${orderId}`, {
      method: 'POST',
      headers: authHeaders()
    });
    return handle(res);
  },

  // ========== STATS + AI ==========
  getStats: async () => {
    const res = await fetch(`${API_URL}/api/progress/stats`, { headers: authHeaders() });
    return handle(res);
  },
  getAIRecommendations: async () => {
    const res = await fetch(`${API_URL}/api/ai/recommendations`, {
      method: 'POST',
      headers: authHeaders()
    });
    return handle(res);
  },

  // ========== COACH IA CHAT (persist conversation) ==========
  coachSend: async (message, conversationId = null) => {
    const res = await fetch(`${API_URL}/api/coach/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ message, conversation_id: conversationId })
    });
    return handle(res);
  },
  coachListConversations: async () => {
    const res = await fetch(`${API_URL}/api/coach/conversations`, { headers: authHeaders() });
    return handle(res);
  },
  coachGetConversation: async (id) => {
    const res = await fetch(`${API_URL}/api/coach/conversations/${id}`, { headers: authHeaders() });
    return handle(res);
  },
  coachDeleteConversation: async (id) => {
    const res = await fetch(`${API_URL}/api/coach/conversations/${id}`, {
      method: 'DELETE', headers: authHeaders()
    });
    return handle(res);
  },
  coachRenameConversation: async (id, title) => {
    const res = await fetch(`${API_URL}/api/coach/conversations/${id}/rename`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ title })
    });
    return handle(res);
  },

  // ========== AI ==========
  correctAnswer: async (question, choices, correctAnswer, userAnswer, theme) => {
    const res = await fetch(`${API_URL}/api/ai/correct`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ question, choices, correct_answer: correctAnswer, user_answer: userAnswer, theme })
    });
    return handle(res);
  },

  // ========== ADMIN ==========
  getAdminStats: async () => {
    const res = await fetch(`${API_URL}/api/admin/stats`, { headers: authHeaders() });
    return handle(res);
  },
  getAdminUsers: async () => {
    const res = await fetch(`${API_URL}/api/admin/users`, { headers: authHeaders() });
    return handle(res);
  },
  resetUserPassword: async (email, newPassword) => {
    const res = await fetch(`${API_URL}/api/admin/users/${encodeURIComponent(email)}/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ new_password: newPassword })
    });
    return handle(res);
  },
  deleteUser: async (email) => {
    const res = await fetch(`${API_URL}/api/admin/users/${encodeURIComponent(email)}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    return handle(res);
  },
  getTeacherCodes: async () => {
    const res = await fetch(`${API_URL}/api/admin/teacher-codes`, { headers: authHeaders() });
    return handle(res);
  },
  createTeacherCode: async (note) => {
    const res = await fetch(`${API_URL}/api/admin/teacher-codes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ note: note || '' })
    });
    return handle(res);
  },
  deleteTeacherCode: async (code) => {
    const res = await fetch(`${API_URL}/api/admin/teacher-codes/${code}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    return handle(res);
  },

  // ========== TEACHER APPLICATIONS (demandes d'accès enseignant) ==========
  submitTeacherApplication: async (payload) => {
    const res = await fetch(`${API_URL}/api/teacher-applications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    return handle(res);
  },
  getTeacherApplications: async (status) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const res = await fetch(`${API_URL}/api/admin/teacher-applications${qs}`, { headers: authHeaders() });
    return handle(res);
  },
  approveTeacherApplication: async (appId) => {
    const res = await fetch(`${API_URL}/api/admin/teacher-applications/${appId}/approve`, {
      method: 'POST',
      headers: authHeaders()
    });
    return handle(res);
  },
  rejectTeacherApplication: async (appId, reason) => {
    const res = await fetch(`${API_URL}/api/admin/teacher-applications/${appId}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ reason })
    });
    return handle(res);
  },
  deleteTeacherApplication: async (appId) => {
    const res = await fetch(`${API_URL}/api/admin/teacher-applications/${appId}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    return handle(res);
  },

  // ========== PASSWORD RESET REQUESTS ==========
  submitPasswordResetRequest: async (email, message) => {
    const res = await fetch(`${API_URL}/api/password-reset-requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, message: message || null })
    });
    return handle(res);
  },
  getPasswordResetRequests: async (status) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const res = await fetch(`${API_URL}/api/admin/password-reset-requests${qs}`, { headers: authHeaders() });
    return handle(res);
  },
  resolvePasswordResetRequest: async (reqId) => {
    const res = await fetch(`${API_URL}/api/admin/password-reset-requests/${reqId}/resolve`, {
      method: 'POST',
      headers: authHeaders()
    });
    return handle(res);
  },
  deletePasswordResetRequest: async (reqId) => {
    const res = await fetch(`${API_URL}/api/admin/password-reset-requests/${reqId}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    return handle(res);
  },

  // ========== ANALYTICS ==========
  trackView: async (visitorId, path) => {
    try {
      await fetch(`${API_URL}/api/track/view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ visitor_id: visitorId, path })
      });
    } catch { /* silent — tracking should never block UX */ }
  },
  getAnalyticsOverview: async () => {
    const res = await fetch(`${API_URL}/api/admin/analytics/overview`, { headers: authHeaders() });
    return handle(res);
  },
  getAnalyticsTimeline: async (days = 30) => {
    const res = await fetch(`${API_URL}/api/admin/analytics/timeline?days=${days}`, { headers: authHeaders() });
    return handle(res);
  },
  getAnalyticsTopPages: async (limit = 10, days = 30) => {
    const res = await fetch(`${API_URL}/api/admin/analytics/top-pages?limit=${limit}&days=${days}`, { headers: authHeaders() });
    return handle(res);
  },

  // ========== BACK-OFFICE B2B (CRM + Devis/Facture/Contrat) ==========
  // Toutes ces routes sont admin-only (rôle "admin" requis côté backend).
  b2bGetStats: async () => {
    const res = await fetch(`${API_URL}/api/admin/b2b/stats`, { headers: authHeaders() });
    return handle(res);
  },
  // Leads (demandes /prestations-pro non encore importées dans le CRM)
  b2bListLeads: async (limit = 100) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/leads?limit=${limit}`, { headers: authHeaders() });
    return handle(res);
  },
  b2bImportLead: async (leadId) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/leads/${leadId}/import`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handle(res);
  },
  // Clients
  b2bListClients: async (q = '') => {
    const url = new URL(`${API_URL}/api/admin/b2b/clients`);
    if (q) url.searchParams.set('q', q);
    const res = await fetch(url.toString(), { headers: authHeaders() });
    return handle(res);
  },
  b2bCreateClient: async (payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/clients`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  b2bGetClient: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/clients/${id}`, { headers: authHeaders() });
    return handle(res);
  },
  b2bUpdateClient: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/clients/${id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  b2bDeleteClient: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/clients/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return handle(res);
  },
  // Documents (Devis / Facture / Contrat)
  b2bListDocuments: async ({ type, status, clientId } = {}) => {
    const url = new URL(`${API_URL}/api/admin/b2b/documents`);
    if (type) url.searchParams.set('type', type);
    if (status) url.searchParams.set('status', status);
    if (clientId) url.searchParams.set('client_id', clientId);
    const res = await fetch(url.toString(), { headers: authHeaders() });
    return handle(res);
  },
  b2bCreateDocument: async (payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/documents`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  b2bGetDocument: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/documents/${id}`, { headers: authHeaders() });
    return handle(res);
  },
  b2bUpdateDocument: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/documents/${id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  b2bUpdateDocumentStatus: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/documents/${id}/status`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  b2bConvertDevisToFacture: async (devisId) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/documents/${devisId}/duplicate-as-facture`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handle(res);
  },
  // Téléchargement : on retourne un blob pour déclencher save côté navigateur.
  b2bDownloadDocument: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/documents/${id}/download`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';
    const m = cd.match(/filename="([^"]+)"/);
    return { blob, filename: m ? m[1] : 'document.pdf' };
  },
  b2bSendDocument: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/documents/${id}/send`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    return handle(res);
  },
  b2bDeleteDocument: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/documents/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return handle(res);
  },
  // Flyers (upload + envoi)
  b2bListFlyers: async ({ q, tag } = {}) => {
    const url = new URL(`${API_URL}/api/admin/b2b/flyers`);
    if (q) url.searchParams.set('q', q);
    if (tag) url.searchParams.set('tag', tag);
    const res = await fetch(url.toString(), { headers: authHeaders() });
    return handle(res);
  },
  b2bUploadFlyer: async ({ title, description, tags, file }) => {
    const form = new FormData();
    form.append('title', title || '');
    form.append('description', description || '');
    form.append('tags', tags || '');
    form.append('file', file);
    const res = await fetch(`${API_URL}/api/admin/b2b/flyers/upload`, {
      method: 'POST',
      headers: authHeaders(), // ne PAS définir Content-Type → boundary auto
      body: form,
    });
    return handle(res);
  },
  // URL inline (preview iframe) avec token via header impossible → on télécharge en blob
  b2bPreviewFlyer: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/flyers/${id}/preview`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob); // à révoquer après fermeture
  },
  b2bDownloadFlyer: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/flyers/${id}/download`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';
    const m = cd.match(/filename="?([^"]+)"?/);
    return { blob, filename: m ? m[1] : 'flyer' };
  },
  b2bUpdateFlyer: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/flyers/${id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  b2bDeleteFlyer: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/flyers/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return handle(res);
  },

  // ========== B2B DOCUMENTS (devis / contrat / facture + signature) ==========
  b2bDocCatalog: async () => {
    const res = await fetch(`${API_URL}/api/admin/b2b-docs/catalog`, { headers: authHeaders() });
    return handle(res);
  },
  b2bDocList: async () => {
    const res = await fetch(`${API_URL}/api/admin/b2b-docs`, { headers: authHeaders() });
    return handle(res);
  },
  b2bDocGenerate: async (payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b-docs/generate`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  b2bDocGetAdmin: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/b2b-docs/${id}`, { headers: authHeaders() });
    return handle(res);
  },
  b2bDocDelete: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/b2b-docs/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return handle(res);
  },
  b2bDocPatch: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b-docs/${id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  // --- Partenaire public (via token) ---
  b2bDocPublicGet: async (token) => {
    const res = await fetch(`${API_URL}/api/b2b-docs/${token}`);
    return handle(res);
  },
  b2bDocPublicSign: async (token, payload) => {
    const res = await fetch(`${API_URL}/api/b2b-docs/${token}/sign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },

  // ========== VCARD / Carte de visite digitale + tracking ==========
  vcardList: async () => {
    const res = await fetch(`${API_URL}/api/admin/vcard/prospects`, { headers: authHeaders() });
    return handle(res);
  },
  vcardCreate: async (payload) => {
    const res = await fetch(`${API_URL}/api/admin/vcard/prospects`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  vcardGetAdmin: async (slug) => {
    const res = await fetch(`${API_URL}/api/admin/vcard/prospects/${slug}`, { headers: authHeaders() });
    return handle(res);
  },
  vcardDelete: async (slug) => {
    const res = await fetch(`${API_URL}/api/admin/vcard/prospects/${slug}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return handle(res);
  },
  // Public
  vcardGetProspectPublic: async (slug) => {
    const res = await fetch(`${API_URL}/api/vcard/prospect/${slug}`);
    return handle(res);
  },
  vcardRegisterVisit: async (slug) => {
    try {
      await fetch(`${API_URL}/api/vcard/visit/${slug}`, { method: 'POST' });
    } catch {}
  },

  b2bSendFlyer: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/flyers/${id}/send`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  // Agenda (planning + événements)
  b2bAgendaMeta: async () => {
    const res = await fetch(`${API_URL}/api/admin/b2b/agenda/meta`, { headers: authHeaders() });
    return handle(res);
  },
  b2bAgendaList: async ({ start, end }) => {
    const url = new URL(`${API_URL}/api/admin/b2b/agenda`);
    url.searchParams.set('start', start);
    url.searchParams.set('end', end);
    const res = await fetch(url.toString(), { headers: authHeaders() });
    return handle(res);
  },
  b2bCreateEvent: async (payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/agenda/events`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  b2bUpdateEvent: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/agenda/events/${id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  b2bDeleteEvent: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/agenda/events/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return handle(res);
  },
  b2bScheduleLead: async (leadId, payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/leads/${leadId}/schedule`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  b2bScheduleDocument: async (docId, payload) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/documents/${docId}/schedule`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },

  // ========== BACK-OFFICE (Phase 29 — import codevoiture.fr) ==========
  // Namespace isolé /api/bo/* — Secrétariat, planning, inbox admin.
  boHealth: async () => {
    const res = await fetch(`${API_URL}/api/bo/health`);
    return handle(res);
  },
  boInboundStatus: async () => {
    const res = await fetch(`${API_URL}/api/inbound-email/status`);
    return handle(res);
  },
  boSeedDemo: async () => {
    const res = await fetch(`${API_URL}/api/bo/admin/seed-demo`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handle(res);
  },
  boSeedVoitureContent: async () => {
    const res = await fetch(`${API_URL}/api/bo/admin/seed-voiture-content`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handle(res);
  },
  // Secrétariat — Formules & Stats
  boGetFormules: async () => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/formules`, { headers: authHeaders() });
    return handle(res);
  },
  boGetSecretariatStats: async () => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/stats`, { headers: authHeaders() });
    return handle(res);
  },
  // Secrétariat — Élèves
  boListEleves: async () => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/eleves`, { headers: authHeaders() });
    return handle(res);
  },
  boCreateEleve: async (payload) => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/eleves`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  boUpdateEleve: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/eleves/${id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  boDeleteEleve: async (id) => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/eleves/${id}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    return handle(res);
  },
  // Moniteur (enseignant) — Élèves
  boMoniteurListEleves: async () => {
    const res = await fetch(`${API_URL}/api/bo/moniteur/eleves`, { headers: authHeaders() });
    return handle(res);
  },
  boMoniteurCreateEleve: async (payload) => {
    const res = await fetch(`${API_URL}/api/bo/moniteur/eleves`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  // Secrétariat — Enseignants
  boListEnseignants: async () => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/enseignants`, { headers: authHeaders() });
    return handle(res);
  },
  boCreateEnseignant: async (payload) => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/enseignants`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  boUpdateEnseignant: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/enseignants/${id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  boDeleteEnseignant: async (id) => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/enseignants/${id}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    return handle(res);
  },
  // Secrétariat — Leçons (planning global)
  boListLessons: async ({ dateFrom, dateTo } = {}) => {
    const p = new URLSearchParams();
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    const url = `${API_URL}/api/bo/secretariat/lessons${p.toString() ? `?${p.toString()}` : ''}`;
    const res = await fetch(url, { headers: authHeaders() });
    return handle(res);
  },
  boCreateLesson: async (payload) => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/lessons`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  boUpdateLesson: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/lessons/${id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  boDeleteLesson: async (id) => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/lessons/${id}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    return handle(res);
  },
  boGetSettings: async () => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/settings`, { headers: authHeaders() });
    return handle(res);
  },
  boUpdateSettings: async (payload) => {
    const res = await fetch(`${API_URL}/api/bo/secretariat/settings`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  // Planning enseignant (mon planning)
  boMyPlanning: async () => {
    const res = await fetch(`${API_URL}/api/bo/planning/my-planning`, { headers: authHeaders() });
    return handle(res);
  },
  boPlanningAdd: async (payload) => {
    const res = await fetch(`${API_URL}/api/bo/planning/add`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  boPlanningReschedule: async (lessonId, payload) => {
    const res = await fetch(`${API_URL}/api/bo/planning/${lessonId}/reschedule`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  boPlanningCancel: async (lessonId, status = 'annule') => {
    const res = await fetch(`${API_URL}/api/bo/planning/${lessonId}/cancel`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    return handle(res);
  },
  boPlanningComplete: async (lessonId) => {
    const res = await fetch(`${API_URL}/api/bo/planning/${lessonId}/complete`, {
      method: 'POST', headers: authHeaders(),
    });
    return handle(res);
  },
  // Bilan de leçon (Phase 29.E)
  boLessonBilanPost: async (lessonId, payload) => {
    const res = await fetch(`${API_URL}/api/bo/planning/${lessonId}/bilan`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  boLessonBilanGet: async (lessonId) => {
    const res = await fetch(`${API_URL}/api/bo/planning/${lessonId}/bilan`, { headers: authHeaders() });
    return handle(res);
  },
  // Espace Élève
  boEleveDashboard: async () => {
    const res = await fetch(`${API_URL}/api/bo/eleve/my-dashboard`, { headers: authHeaders() });
    return handle(res);
  },
  boEleveHistory: async () => {
    const res = await fetch(`${API_URL}/api/bo/eleve/my-history`, { headers: authHeaders() });
    return handle(res);
  },
  boEleveCompetences: async () => {
    const res = await fetch(`${API_URL}/api/bo/eleve/competences`);
    return handle(res);
  },
  // Admin Inbox (Resend inbound — squelette)
  boListInbox: async (includeArchived = false) => {
    const res = await fetch(`${API_URL}/api/bo/admin/inbox?archived=${includeArchived}`, { headers: authHeaders() });
    return handle(res);
  },
  boPatchInbox: async (mailId, payload) => {
    const res = await fetch(`${API_URL}/api/bo/admin/inbox/${mailId}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  boReplyInbox: async (mailId, body, subject) => {
    const res = await fetch(`${API_URL}/api/bo/admin/inbox/${mailId}/reply`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ body, subject: subject || '' }),
    });
    return handle(res);
  },
  boDeleteInbox: async (mailId) => {
    const res = await fetch(`${API_URL}/api/bo/admin/inbox/${mailId}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    return handle(res);
  },

  // ═══ Planning personnel autonome (Phase 30.C) ═══
  persoCreateEvent: async (payload) => {
    const res = await fetch(`${API_URL}/api/perso/events`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  persoListEvents: async ({ dateFrom, dateTo } = {}) => {
    const params = new URLSearchParams();
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const res = await fetch(`${API_URL}/api/perso/events?${params.toString()}`, { headers: authHeaders() });
    return handle(res);
  },
  persoUpdateEvent: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/perso/events/${id}`, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  persoDeleteEvent: async (id) => {
    const res = await fetch(`${API_URL}/api/perso/events/${id}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    return handle(res);
  },
  persoShareEvent: async (id) => {
    const res = await fetch(`${API_URL}/api/perso/events/${id}/share`, {
      method: 'POST', headers: authHeaders(),
    });
    return handle(res);
  },
  persoGetSharedByToken: async (token) => {
    const res = await fetch(`${API_URL}/api/perso/share/${token}`);
    return handle(res);
  },
  persoAcceptShared: async (token) => {
    const res = await fetch(`${API_URL}/api/perso/share/${token}/accept`, {
      method: 'POST', headers: authHeaders(),
    });
    return handle(res);
  },
  persoListMesEleves: async () => {
    const res = await fetch(`${API_URL}/api/perso/mes-eleves`, { headers: authHeaders() });
    return handle(res);
  },

  // ═══ Demandes internes (Phase 30.E) ═══
  internalRequestCreate: async (payload) => {
    const res = await fetch(`${API_URL}/api/internal-requests/`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  internalRequestListMine: async () => {
    const res = await fetch(`${API_URL}/api/internal-requests/mine`, { headers: authHeaders() });
    return handle(res);
  },
  internalRequestListAdmin: async ({ status, from_role } = {}) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (from_role) params.set('from_role', from_role);
    const res = await fetch(`${API_URL}/api/internal-requests/admin?${params.toString()}`, { headers: authHeaders() });
    return handle(res);
  },
  internalRequestCounts: async () => {
    const res = await fetch(`${API_URL}/api/internal-requests/admin/counts`, { headers: authHeaders() });
    return handle(res);
  },
  internalRequestReply: async (id, reply, status = 'resolved') => {
    const res = await fetch(`${API_URL}/api/internal-requests/${id}/reply`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ reply, status }),
    });
    return handle(res);
  },

  // ========== MONITEUR PROFILE + QR (Phase 31) ==========
  moniteurProfileGetMe: async () => {
    const res = await fetch(`${API_URL}/api/moniteur-profile/me`, { headers: authHeaders() });
    return handle(res);
  },
  moniteurProfilePatchMe: async (payload) => {
    const res = await fetch(`${API_URL}/api/moniteur-profile/me`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  moniteurProfileRegenerateQr: async () => {
    const res = await fetch(`${API_URL}/api/moniteur-profile/regenerate-qr`, {
      method: 'POST', headers: authHeaders(),
    });
    return handle(res);
  },
  moniteurProfileToggleQr: async () => {
    const res = await fetch(`${API_URL}/api/moniteur-profile/toggle-qr`, {
      method: 'POST', headers: authHeaders(),
    });
    return handle(res);
  },
  moniteurProfileJoin: async (token, payload = {}) => {
    const res = await fetch(`${API_URL}/api/moniteur-profile/join/${encodeURIComponent(token)}`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  moniteurProfileRevoke: async (studentId) => {
    const res = await fetch(`${API_URL}/api/moniteur-profile/revoke/${encodeURIComponent(studentId)}`, {
      method: 'POST', headers: authHeaders(),
    });
    return handle(res);
  },
  // Public — pas d'auth
  publicMoniteurByQr: async (token) => {
    const res = await fetch(`${API_URL}/api/public/moniteur/qr/${encodeURIComponent(token)}`);
    return handle(res);
  },

  // ========== FICHES MONITEUR INFORMELLES (élève, Phase 31.B) ==========
  eleveMoniteursList: async () => {
    const res = await fetch(`${API_URL}/api/eleve-moniteurs/mine`, { headers: authHeaders() });
    return handle(res);
  },
  eleveMoniteursCreate: async (payload) => {
    const res = await fetch(`${API_URL}/api/eleve-moniteurs/`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  eleveMoniteursDelete: async (id) => {
    const res = await fetch(`${API_URL}/api/eleve-moniteurs/${encodeURIComponent(id)}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    return handle(res);
  },

  // ========== B2B PLAQUETTE (plaquette commerciale protégée) ==========
  b2bPlaquetteAccess: async (token = null) => {
    // Public endpoint — tries JWT admin header first, falls back to token param.
    const res = await fetch(`${API_URL}/api/b2b/plaquette/access`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
      },
      body: JSON.stringify(token ? { token } : {}),
    });
    return handle(res);
  },
  b2bPlaquetteVaultInfo: async () => {
    const res = await fetch(`${API_URL}/api/admin/b2b/plaquette/vault-info`, { headers: authHeaders() });
    return handle(res);
  },
  b2bPlaquetteListTokens: async () => {
    const res = await fetch(`${API_URL}/api/admin/b2b/plaquette/tokens`, { headers: authHeaders() });
    return handle(res);
  },
  b2bPlaquetteCreateToken: async (label, days = 30) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/plaquette/tokens`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ label, days }),
    });
    return handle(res);
  },
  b2bPlaquetteRevokeToken: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/b2b/plaquette/tokens/${encodeURIComponent(id)}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    return handle(res);
  },

  // ========== SHORTS (vidéos verticales communautaires, Phase 36) ==========
  shortsFeed: async (theme = null) => {
    const q = theme ? `?theme=${encodeURIComponent(theme)}` : '';
    const res = await fetch(`${API_URL}/api/shorts/feed${q}`);
    return handle(res);
  },
  shortsViews: async () => {
    const res = await fetch(`${API_URL}/api/shorts/views`);
    return handle(res);
  },
  shortsRecordView: async (shortId, sessionId) => {
    const res = await fetch(`${API_URL}/api/shorts/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ short_id: shortId, session_id: sessionId }),
    });
    return handle(res);
  },
  shortsLikes: async () => {
    const res = await fetch(`${API_URL}/api/shorts/likes`);
    return handle(res);
  },
  shortsToggleLike: async (shortId, sessionId) => {
    const res = await fetch(`${API_URL}/api/shorts/${encodeURIComponent(shortId)}/like`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    });
    return handle(res);
  },
  shortsReport: async (shortId, reason, note = '') => {
    const res = await fetch(`${API_URL}/api/shorts/${encodeURIComponent(shortId)}/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason, note }),
    });
    return handle(res);
  },
  shortsSubmit: async (payload) => {
    // payload = { title, description, theme, url, cgu_accepted }
    const res = await fetch(`${API_URL}/api/shorts/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  shortsUpload: async (formData) => {
    // formData = FormData avec file, title, description, theme, cgu_accepted
    const res = await fetch(`${API_URL}/api/shorts/upload`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });
    return handle(res);
  },
  shortsMine: async () => {
    const res = await fetch(`${API_URL}/api/shorts/mine`, { headers: authHeaders() });
    return handle(res);
  },
  adminShortsList: async (status = 'pending') => {
    const res = await fetch(`${API_URL}/api/admin/shorts?status=${encodeURIComponent(status)}`, {
      headers: authHeaders(),
    });
    return handle(res);
  },
  adminShortsPublish: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/shorts/${encodeURIComponent(id)}/publish`, {
      method: 'POST', headers: authHeaders(),
    });
    return handle(res);
  },
  adminShortsReject: async (id, reason = '') => {
    const res = await fetch(`${API_URL}/api/admin/shorts/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ reason }),
    });
    return handle(res);
  },
  adminShortsRetire: async (id, reason = '') => {
    const res = await fetch(`${API_URL}/api/admin/shorts/${encodeURIComponent(id)}/retire`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ reason }),
    });
    return handle(res);
  },
  adminShortsReports: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/shorts/${encodeURIComponent(id)}/reports`, {
      headers: authHeaders(),
    });
    return handle(res);
  },
  shortsMineEdit: async (id, patch) => {
    const res = await fetch(`${API_URL}/api/shorts/mine/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(patch),
    });
    return handle(res);
  },
  shortsMineDelete: async (id) => {
    const res = await fetch(`${API_URL}/api/shorts/mine/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return handle(res);
  },
  // === Enseignant (back-office pédagogique) ===
  teacherShortsList: async (status = 'all') => {
    const res = await fetch(`${API_URL}/api/moniteur/shorts?status=${encodeURIComponent(status)}`, {
      headers: authHeaders(),
    });
    return handle(res);
  },
  teacherShortsFeedback: async (id, payload) => {
    const res = await fetch(`${API_URL}/api/moniteur/shorts/${encodeURIComponent(id)}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  // === Admin — statistiques dédiées Shorts ===
  adminShortsStats: async () => {
    const res = await fetch(`${API_URL}/api/admin/shorts/stats`, { headers: authHeaders() });
    return handle(res);
  },
  adminShortsCounts: async () => {
    const res = await fetch(`${API_URL}/api/admin/shorts/counts`, { headers: authHeaders() });
    return handle(res);
  },
};

export default api;
export { API_URL };
